/* Stage 3 — cluster merging (Fudos–Hoffmann, generalised) → plan → replay.
 *
 * Decomposition and replay run in the core.  `PlanSolver` compiles once per topology and replays
 * the plan on every solve; `PlanDrag` is the DCM-style drag that never re-analyses the graph. */
import { Constraint } from './constraints.js';
import { Point, Sketch } from './model.js';
import {
  Drag, Method, RESULT_LEN, SolveResult, System, Triangle, guardBuffer, now, readFlips,
  readResult,
} from './system.js';
import { core, takeJson, takeStr, withBuf } from './wasm.js';

const METHOD_ID: Record<Method, number> = { dogleg: 0, lm: 1 };

/** A geometric element of the F–H graph: a point class, a line, or a virtual radius line. */
export type El = ['P' | 'L' | 'V', number];

/** One merge, lowered for replay.  `ppp` is the (x, y, z) closed-form construction and `branch`
 *  its ±1 chirality; `key` is the document-stable identity that persists. */
export interface Step {
  ids: number[];
  ppp: [El, El, El] | null;
  branch: number | null;
  key: string | null;
  nPairs: number;
  nDpairs: number;
}

export interface Plan {
  leaves: number;
  steps: Step[];
  roots: number[];
  fullyDecomposed: boolean;
  stickyBranches: boolean;
  summary: string;
  pppTriangles: [number, number, number][];
}

export interface Graph {
  nPoints: number;
  members: number[][];
  lines: number[];
  virtuals: [El, El][];
  edges: { kind: 'PP' | 'PL'; a: El; b: El; source: number | null }[];
  dirs: { a: El; b: El; phi: number; source: number }[];
  unsupported: number[];
  knownRadius: Record<string, number>;
  groundPoints: number[];
  passive: number[];
  summary: string;
}

export interface PlanResult {
  success: boolean;
  maxResidual: number;
  fellBack: boolean;
  timeS: number;
  nSteps: number;
  numeric: SolveResult | null;
  plan: Plan;
}

/** The same outcome in the solver's common result type (method `plan` or the fallback's). */
export function asSolveResult(pr: PlanResult): SolveResult {
  if (pr.numeric) return pr.numeric;
  return {
    success: pr.success, settled: pr.success, status: 0, message: 'plan',
    residualNorm: pr.maxResidual, maxResidual: pr.maxResidual,
    nfev: pr.nSteps, njev: 0, timeS: pr.timeS, method: 'plan', iterations: 0, rank: null,
  };
}

/** The F–H constraint graph of a sketch (elements, edges, direction relations). */
export function buildGraph(sketch: Sketch): Graph {
  return takeJson<Graph>(core().gcs_graph_json(sketch.handle));
}

/** Compile once per topology (graph + decomposition + a System for verification); `solve` replays
 *  the plan and falls back to the numeric core when the residual says the plan did not (fully)
 *  determine the sketch. */
export class PlanSolver {
  /** The core handle — a drag made on this plan passes it through, and never outlives it. */
  handle: number;
  /** Owned by this PlanSolver — borrow it (diagnosis does), never dispose it. */
  readonly system: System;

  constructor(readonly sketch: Sketch, sticky = false) {
    this.handle = core().gcs_plan_solver_new(sketch.handle, sticky ? 1 : 0);
    this.system = new System(sketch, core().gcs_plan_solver_system(this.handle), false);
  }

  dispose(): void {
    if (!this.handle) return;
    this.system.dispose();
    core().gcs_plan_solver_free(this.handle);
    this.handle = 0;
  }

  get plan(): Plan {
    return takeJson<Plan>(core().gcs_plan_solver_plan_json(this.handle));
  }

  get graph(): Graph {
    return takeJson<Graph>(core().gcs_plan_solver_graph_json(this.handle));
  }

  set stickyBranches(v: boolean) {
    core().gcs_plan_solver_sticky(this.handle, v ? 1 : 0);
  }

  /** The point element (coincidence class) a sketch point belongs to. */
  pointElement(p: Point): number {
    return core().gcs_plan_solver_point_element(this.handle, p.index);
  }

  /** Flip the root of every closed-form construction that places `p`; returns how many.  Root
   *  choices are document state — `solve` reads them back. */
  flip(p: Point): number {
    return core().gcs_plan_solver_flip(this.handle, this.sketch.handle, p.index);
  }

  /** Replay the plan on the current geometry (no solving, no fallback). */
  execute(): void {
    core().gcs_plan_solver_execute(this.handle, this.sketch.handle);
  }

  branches(): Map<string, number> {
    const out = new Map<string, number>();
    for (const st of this.plan.steps) {
      if (st.key !== null && st.branch !== null) out.set(st.key, st.branch);
    }
    return out;
  }

  /** The closed-form merges' triangles — the order-type invariants a numeric drag guards. */
  pppTriangles(): Triangle[] {
    const pts = this.sketch.points;
    return this.plan.pppTriangles.map(([a, b, c]) => [pts[a], pts[b], pts[c]] as Triangle);
  }

  /** (index, step) of every merge that places `p`: closed-form ones where it is the constructed
   *  apex, else numeric merges that share it. */
  stepsPlacing(p: Point): [number, Step][] {
    const steps = this.plan.steps;
    const idxs = withBuf(Math.max(steps.length, 1), 4, (b) => {
      const n = core().gcs_plan_steps_placing(this.handle, p.index, b.ptr);
      return [...b.i32.subarray(0, n)];
    });
    return idxs.map((i) => [i, steps[i]] as [number, Step]);
  }

  solve(tol = 1e-9, fallback = true, method: Method = 'dogleg'): PlanResult {
    const t0 = now();
    const d = takeJson<{ success: boolean; maxResidual: number; fellBack: boolean;
                         nSteps: number; numeric: SolveResult | null }>(
      core().gcs_plan_solver_solve(this.handle, this.sketch.handle, tol, fallback ? 1 : 0,
                                   METHOD_ID[method]));
    return { ...d, timeS: now() - t0, plan: this.plan };
  }
}

/** DCM-style drag: the plan's roots move as rigid bodies, so a frame costs the region of the
 *  sketch the drag reaches and nothing else — no graph analysis while dragging, recorded roots
 *  stay as they are, and under-constrained bodies move least.  Where the plan cannot carry the
 *  drag (unsupported constraints, a region too large for the rigid-body solve) the numeric
 *  pull/polish `Drag` takes over.
 *
 *  Given the sketch's own `PlanSolver` — the one its owner caches per topology, which must
 *  outlive the drag — it starts at once and runs on the sketch directly.  Without one, the core
 *  builds a plan over the dragged point's *part* of the document — what is connected to it — and
 *  writes each frame back.  Either way the document is never restructured by a drag, and figures
 *  unrelated to the point cost it nothing. */
export class PlanDrag {
  /** Drags made and not yet ended — a handle that outlives its gesture shows up here. */
  static live = 0;
  private handle: number;
  active = true;

  /** Held, not just read: the core keeps this plan's pointer for the drag's lifetime, so the
   *  drag has to keep the plan from being collected or disposed under it. */
  readonly plan: PlanSolver | null;

  /** `eye` drags a point in space where the eye at that bearing and elevation sees it: `x`, `y`
   *  and every `move` are then on the eye's picture plane, and `guards` are not asked. */
  constructor(readonly sketch: Sketch, readonly point: Point, x: number, y: number,
              guards: Triangle[] | null = null, maxStepRel = 0.05, plan: PlanSolver | null = null,
              eye: { az: number; el: number } | null = null) {
    this.plan = plan;
    const ps = plan ? plan.handle : 0;
    this.handle = eye
      ? core().gcs_plan_drag_seen(sketch.handle, ps, point.index, x, y, eye.az, eye.el, maxStepRel)
      : guardBuffer(guards, (ptr, n) =>
          core().gcs_plan_drag_new(sketch.handle, ps, point.index, x, y, ptr, n, maxStepRel));
    PlanDrag.live += 1;
  }

  /** True while the cached plan is driving the drag (false once it handed over). */
  get usable(): boolean {
    return core().gcs_plan_drag_usable(this.handle) !== 0;
  }

  /** Truthy once the numeric drag has taken over. */
  get numeric(): boolean {
    return !this.usable;
  }

  get flips(): Triangle[] {
    const c = core();
    return readFlips(this.sketch, (d, o) => c.gcs_plan_drag_flip_list(d, o), this.handle,
                     c.gcs_plan_drag_flips(this.handle));
  }

  /** Order-type invariants for the numeric path — computed at most once per drag. */
  guardTriangles(): Triangle[] {
    return withBuf(3 * 4096, 4, (b) => {
      const n = core().gcs_plan_drag_guards(this.handle, this.sketch.handle, b.ptr);
      const v = Int32Array.from(b.i32);   // before `sketch.points`, which can grow the heap
      const pts = this.sketch.points;
      const out: Triangle[] = [];
      for (let i = 0; i < n; i++) out.push([pts[v[3 * i]], pts[v[3 * i + 1]], pts[v[3 * i + 2]]]);
      return out;
    });
  }

  branches(): Map<string, number> {
    const o = takeJson<Record<string, number>>(core().gcs_plan_drag_branches_json(this.handle));
    return new Map(Object.entries(o ?? {}));
  }

  move(x: number, y: number): SolveResult {
    const t0 = now();
    return withBuf(RESULT_LEN, 8, (b) => {
      const msg = takeStr(core().gcs_plan_drag_move(this.handle, this.sketch.handle, x, y, b.ptr));
      return readResult(b.f64, msg, 'plan', t0);
    });
  }

  end(): void {
    if (!this.active) return;
    this.active = false;
    PlanDrag.live -= 1;
    core().gcs_plan_drag_end(this.handle, this.sketch.handle);
    core().gcs_plan_drag_free(this.handle);
    this.handle = 0;
  }
}

export { Drag, System };
export type { Constraint };
